import { useFeatureIsOn } from '@frontend/react';
import { Icon } from '@frontend/web-react';
import type { IconName } from '@frontend/web-react/components/Primitives/Icon/helpers';
import { useAnalytics } from '@frontend/web/hooks';
import { useMatchRoute, useNavigate } from '@tanstack/react-router';

type NavItem = {
  activeIcon?: IconName;
  button?: boolean;
  icon: IconName;
  label: string;
  onClick?: () => void;
  to?: string;
};

export const BottomNav = () => {
  const navigate = useNavigate();
  const matchRoute = useMatchRoute();
  const { track } = useAnalytics();
  const { enabled: isDiscoverTabEnabled } = useFeatureIsOn('is_discover_tab_enabled');

  const isHomeActive = !!matchRoute({ fuzzy: true, to: '/app/home' });
  const isTasksActive = !!matchRoute({ fuzzy: true, to: '/app/task' });
  const isDiscoverActive = !!matchRoute({ fuzzy: true, to: '/app/discover' });
  const isSettingsActive = !!matchRoute({ fuzzy: true, to: '/app/settings' });
  const navItems: NavItem[] = [
    {
      icon: isHomeActive ? 'HomeIconActive' : 'HomeIcon',
      label: 'Home',
      to: '/app/home',
    },
    {
      icon: isTasksActive ? 'TaskIconActive' : 'TaskIcon',
      label: 'Tasks',
      to: '/app/task',
    },
    {
      button: true,
      icon: 'IconPlus',
      label: 'New',
      onClick: () => {
        track({ data: { entryPoint: 'bottom_nav_cta' }, event: 'taskCreationStarted', topic: 'draftTasks' });
        navigate({ to: '/app/draft-task/create' });
      },
    },
    ...(isDiscoverTabEnabled
      ? [
          {
            icon: (isDiscoverActive ? 'DiscoverIconActive' : 'DiscoverIcon') as IconName,
            label: 'Discover',
            to: '/app/discover',
          },
        ]
      : []),
    {
      icon: isSettingsActive ? 'IconSettingsFilled' : 'IconSettings',
      label: 'Settings',
      to: '/app/settings/notifications',
    },
  ];

  const handleNavClick = (item: NavItem) => {
    if (item.onClick) {
      item.onClick();
    } else if (item.to) {
      navigate({ to: item.to });
    }
  };

  return (
    <nav
      className="w-full bg-white border-t"
      style={{
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.1)',
        flexShrink: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div className="grid grid-cols-5" style={{ height: '60px' }}>
        {navItems.map((item) => {
          const isActive =
            (item.to === '/app/home' && isHomeActive) ||
            (item.to === '/app/task' && isTasksActive) ||
            (item.to === '/app/discover' && isDiscoverActive) ||
            (item.to === '/app/settings/notifications' && isSettingsActive);

          if (item.button) {
            return (
              <div className="flex items-center justify-center" key={item.label}>
                <button
                  className="flex items-center justify-center rounded-full p-sm transition-transform active:scale-95 bg-neutral-950"
                  onClick={() => handleNavClick(item)}
                  type="button"
                >
                  <Icon color="white" name={item.icon} size={30} />
                </button>
              </div>
            );
          }

          return (
            <button
              className="flex flex-col items-center justify-center gap-xs  transition-all"
              key={item.label}
              onClick={() => handleNavClick(item)}
              type="button"
            >
              <Icon color={isActive ? 'primary' : 'secondary'} name={item.icon} size={24} />
              <span className="text-caption font-medium leading-none">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
